import { useEffect, type ReactNode } from "react";
import { motion } from "framer-motion";

/* Shared pieces for the blue-2 homepage (a copy of HomeBlue in the team's palette roles). */

export const EASE = [0.22, 1, 0.36, 1] as const;
export const CLUB = "/club-preview/blue-2";
export const HOME = "/home-preview/blue-2";

/* The blue-2 site's own pages (preview URLs until they replace the real ones). */
export const PAGES = {
  about: "/blue-2/about", videos: "/blue-2/videos", favorites: "/blue-2/favorites",
  retreats: "/blue-2/retreats", links: "/blue-2/links", shop: "/blue-2/shop",
} as const;
export type PageKey = keyof typeof PAGES | "club";

export function useFonts() {
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght,SOFT@9..144,600..900,0..100&family=Gochi+Hand&family=Amatic+SC:wght@700&family=Figtree:wght@400;500;600;700&display=swap";
    document.head.appendChild(link);
    return () => link.remove();
  }, []);
}

export function Draw({ d, delay = 0, width = 3 }: { d: string; delay?: number; width?: number }) {
  return <motion.path d={d} fill="none" stroke="currentColor" strokeWidth={width} strokeLinecap="round" strokeLinejoin="round"
    initial={{ pathLength: 0, opacity: 0 }} animate={{ pathLength: 1, opacity: 1 }}
    transition={{ pathLength: { duration: 0.55, delay, ease: "easeOut" }, opacity: { duration: 0.01, delay } }} />;
}

// Vertical-only margin: a bare "-80px" also trims the sides, so elements near a phone's edge never enter view.
export const VIEW = { once: true, margin: "-80px 0px" } as const;
export const DENVER = "America/Denver";
export const datePart = (d: Date, o: Intl.DateTimeFormatOptions) => d.toLocaleDateString("en-US", { timeZone: DENVER, ...o });

/** A doodle stroke that draws itself when scrolled into view. */
export function Ink({ d, delay = 0, width = 3 }: { d: string; delay?: number; width?: number }) {
  return <motion.path d={d} fill="none" stroke="currentColor" strokeWidth={width} strokeLinecap="round" strokeLinejoin="round"
    initial={{ pathLength: 0, opacity: 0 }} whileInView={{ pathLength: 1, opacity: 1 }} viewport={VIEW}
    transition={{ pathLength: { duration: 0.6, delay, ease: "easeOut" }, opacity: { duration: 0.01, delay } }} />;
}

export const HEART = "M15 42 C 4 32, 0 22, 3 14 C 6 6, 13 6, 15 14 C 17 6, 25 6, 27 14 C 30 22, 26 32, 15 42 Z";

/** The hero's "Hi, I'm / chelsea" move, reused as every section title. */
export function Head({ id, hand, word, tone, className = "" }: { id: string; hand: string; word: string; tone: "pink" | "blue"; className?: string }) {
  return <h2 id={id} className={`b2-head b2-head-${tone} ${className}`}>
    <motion.span className="b2-head-hand" initial={{ opacity: 0, rotate: -12 }} whileInView={{ opacity: 1, rotate: -5 }} viewport={VIEW} transition={{ delay: 0.25, duration: 0.5, ease: EASE }}>{hand}</motion.span>
    <motion.span className="b2-head-word" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={VIEW} transition={{ duration: 0.7, ease: EASE }}>{word}</motion.span>
  </h2>;
}
